import {handleSessionExpired} from '../modules/auth/core/session'

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.aiiplan.com/api'

// Server-side pagination envelope returned by *Paged list methods.
export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

// Build the ?page=&limit=&search=&<extra> query string for list endpoints. Extra
// holds page-specific server-side filters (role, status, category, …).
function pageQS(page: number, limit: number, search: string, extra?: Record<string, string | undefined>): string {
  const p = new URLSearchParams({page: String(page), limit: String(limit)})
  if (search) p.set('search', search)
  if (extra) for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v)
  return `?${p.toString()}`
}

// A 401 on an authenticated request means our token is no longer accepted
// (expired/revoked) — kick off the shared session-expired flow. Skipped for the
// login call itself, where a 401 just means bad credentials.
function handleUnauthorized(endpoint: string, status: number) {
  if (status === 401 && authToken && !endpoint.startsWith('/auth/')) {
    handleSessionExpired()
  }
}

export interface HostApplication {
  id: string
  status: 'pending' | 'approved' | 'rejected'
  rejectionReason?: string
  fullName: string
  email: string
  phone?: string
  businessName: string
  businessType: string
  address: string
  city: string
  stateRegion: string
  zipCode: string
  yearsExperience?: string
  serviceDescription?: string
  hostPhoto?: string
  portfolioPhotos?: string[]
  packages?: {name: string; pricingModel: string; baseRate: string}[]
  pricingModel?: string
  baseRate?: string
  minBooking?: string
  availability: string[]
  // Documents & Verification
  licenseApplicable?: boolean | null
  insuranceApplicable?: boolean | null
  certificationApplicable?: boolean | null
  businessLicenseUrl?: string | null
  liabilityInsuranceUrl?: string | null
  professionalCertUrl?: string | null
  govIdFrontUrl?: string | null
  govIdBackUrl?: string | null
  legalName?: string | null
  dateOfBirth?: string | null
  ssnLast4?: string | null
  backgroundConsent?: boolean
  verifiedBusinessLicense?: boolean
  verifiedInsurance?: boolean
  verifiedGovId?: boolean
  verifiedCertification?: boolean
  verifiedBackgroundCheck?: boolean
  finalApproved?: boolean
  user: {id: string; firstName: string; lastName: string; email: string}
  createdAt: string
  updatedAt: string
}

export interface ServiceCategory {
  id: string
  label: string
  icon: string
  image?: string | null
  order: number
  isActive: boolean
}

export interface LegalDocument {
  id: string
  slug: string
  title: string
  category: string
  content: string
  order: number
  isPublished: boolean
  createdAt: string
  updatedAt: string
}

export interface Coupon {
  id: string
  code: string
  label: string
  description: string
  discountType: 'fixed' | 'percent'
  discountValue: number
  maxDiscount: number | null
  minSubtotal: number
  active: boolean
  expiresAt: string | null
  usageLimit: number | null
  usedCount: number
  createdAt?: string
  updatedAt?: string
}

export interface CouponInput {
  code?: string
  label?: string
  description?: string
  discountType?: 'fixed' | 'percent'
  discountValue?: number
  maxDiscount?: number | null
  minSubtotal?: number
  active?: boolean
  expiresAt?: string | null
  usageLimit?: number | null
}

export interface Dispute {
  id: string
  disputeRef: string
  source: 'user' | 'stripe'
  status: string
  reason: string
  details: string | null
  amount: number
  transactionRef: string | null
  referenceType: string | null
  refundedAmount: number
  adminNote: string | null
  createdAt: string
  resolvedAt: string | null
  stripeDisputeId?: string | null
  stripeStatus?: string | null
  user: {id: string; firstName: string; lastName: string; email: string} | null
  // Linked ticket/booking details for the admin detail view.
  linked?: {
    kind: 'ticket' | 'booking' | 'unknown'
    kindLabel: string
    entityLabel: string | null
    entityRef: string | null
    entityStatus: string | null
    eventName: string | null
    providerName: string | null
    eventDate: string | null
    entityAmount: number
    cancelable: boolean
  }
}

export interface RedeemCode {
  id: string
  code: string
  label: string
  amount: number
  creditType: 'credit' | 'bonus'
  active: boolean
  expiresAt: string | null
  usageLimit: number | null
  usedCount: number
  oncePerUser: boolean
  createdAt?: string
}

export interface RedeemCodeInput {
  code?: string
  label?: string
  amount?: number
  creditType?: 'credit' | 'bonus'
  active?: boolean
  oncePerUser?: boolean
  expiresAt?: string | null
  usageLimit?: number | null
}

export interface PayoutRequest {
  id: string
  payoutRef: string
  amount: number
  grossSales: number
  commissionRate: number
  status: string
  adminNote: string | null
  processedAt: string | null
  createdAt: string
  user: {id: string; firstName: string; lastName: string; email: string} | null
  payoutMethod: {
    method: 'bank' | 'paypal' | 'card'
    accountName: string | null
    bankName: string | null
    accountNumber: string | null
    routingNumber: string | null
    paypalEmail: string | null
    cardNumber: string | null
    cardLast4: string | null
    cardExpiry: string | null
    cardCvv: string | null
    country: string | null
  } | null
  // Provider payout-wizard fields ('bookings' source). Null for ticket payouts.
  source?: string
  method?: 'bank' | 'card' | null
  feeAmount?: number | null
  processingTime?: string | null
  destinationLabel?: string | null
  destinationLast4?: string | null
}

let authToken: string | null = localStorage.getItem('admin_token')

export const setToken = (token: string) => {
  authToken = token
  localStorage.setItem('admin_token', token)
}

export const clearToken = () => {
  authToken = null
  localStorage.removeItem('admin_token')
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(authToken ? {Authorization: `Bearer ${authToken}`} : {}),
    ...(options.headers as Record<string, string> ?? {}),
  }
  const res = await fetch(`${BASE_URL}${endpoint}`, {...options, headers})
  if (!res.ok) {
    handleUnauthorized(endpoint, res.status)
    const err = await res.json().catch(() => ({}))
    throw new ApiError(res.status, err?.message ?? `HTTP ${res.status}`)
  }
  // DELETE/void endpoints return an empty body — don't choke on res.json().
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

// Multipart variant — lets the browser set the multipart boundary itself, so we
// must NOT send a Content-Type header here (unlike request()).
async function multipart<T>(endpoint: string, method: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers: authToken ? {Authorization: `Bearer ${authToken}`} : {},
    body: form,
  })
  if (!res.ok) {
    handleUnauthorized(endpoint, res.status)
    const err = await res.json().catch(() => ({}))
    throw new ApiError(res.status, err?.message ?? `HTTP ${res.status}`)
  }
  return res.json()
}

export const api = {
  // Auth
  adminLogin: (email: string, password: string) =>
    request<{accessToken: string; user: {id: string; firstName: string; lastName: string; email: string}}>('/auth/admin-login', {
      method: 'POST',
      body: JSON.stringify({email, password}),
    }),

  // Users
  getUsers: () =>
    request<{id: string; firstName: string; lastName: string; email: string; phone: string; photo: string; isEmailVerified: boolean; interests: string[]; createdAt: string; walletBalance: number}[]>('/users'),

  // Interests
  getInterests: () =>
    request<{id: number; label: string; icon: string; order: number; isActive: boolean}[]>('/interests/all'),

  createInterest: (data: {label: string; icon: string; order?: number}) =>
    request<{id: number; label: string; icon: string; order: number; isActive: boolean}>('/interests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateInterest: (id: number, data: {label?: string; icon?: string; order?: number; isActive?: boolean}) =>
    request<{id: number; label: string; icon: string; order: number; isActive: boolean}>(`/interests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteInterest: (id: number) =>
    request<void>(`/interests/${id}`, {method: 'DELETE'}),

  // Event Types — the "Categories" the mobile "Select Event Type" screen uses.
  getEventTypes: () =>
    request<{id: number; label: string; icon: string}[]>('/event-types'),

  // Paginated/searchable list for the admin Categories page. The backend returns
  // the full list (event types are few), so we filter + page on the client to
  // match the Paginated<> shape usePaginated expects.
  getEventTypesPaged: async (page: number, limit: number, search: string) => {
    const all = await request<{id: number; label: string; icon: string; order: number; isActive: boolean}[]>('/event-types/all')
    const term = (search || '').trim().toLowerCase()
    const filtered = term ? all.filter(t => (t.label || '').toLowerCase().includes(term)) : all
    const start = (page - 1) * limit
    return {items: filtered.slice(start, start + limit), total: filtered.length} as Paginated<{id: number; label: string; icon: string; order: number; isActive: boolean}>
  },

  createEventType: (data: {label: string; icon: string; order?: number}) =>
    request<{id: number; label: string; icon: string; order: number; isActive: boolean}>('/event-types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateEventType: (id: number, data: {label?: string; icon?: string; order?: number; isActive?: boolean}) =>
    request<{id: number; label: string; icon: string; order: number; isActive: boolean}>(`/event-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteEventType: (id: number) =>
    request<void>(`/event-types/${id}`, {method: 'DELETE'}),

  // Legal Documents — policy/terms pages edited here and served to the landing
  // page + mobile app. Content is HTML produced by the rich-text editor.
  getLegalDocuments: () =>
    request<LegalDocument[]>('/legal-documents/admin/all'),

  getLegalDocument: (id: string) =>
    request<LegalDocument>(`/legal-documents/admin/${id}`),

  updateLegalDocument: (
    id: string,
    data: {title?: string; category?: string; content?: string; order?: number; isPublished?: boolean},
  ) =>
    request<LegalDocument>(`/legal-documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Events (admin — all events)
  getEvents: () =>
    request<{
      id: string; status: string; eventName?: string;
      eventType?: {id: number; label: string; icon: string};
      eventDate?: string; startTime?: string;
      venueAddress?: string; guestCount?: string;
      user?: {id: string; firstName: string; lastName: string};
      createdAt: string;
    }[]>('/events'),

  deleteEvent: (id: string) =>
    request<void>(`/events/${id}`, {method: 'DELETE'}),

  getAllBookings: () =>
    request<{
      id: string; bookingRef: string; providerName: string; providerCategory: string;
      subtotal: number; depositAmount: number; remainingAmount: number;
      status: string; createdAt: string;
      payments?: {id: string; amount: number; method: string; status: string; type: string}[];
      event?: {id: string; eventName?: string; eventDate?: string; venueAddress?: string};
      eventType?: {id: number; label: string};
      user?: {id: string; firstName: string; lastName: string; email: string};
    }[]>('/events/admin/bookings'),

  getAllTickets: () =>
    request<{
      id: string; ticketRef: string; tier: string; status: string; quantity: number;
      totalAmount: number; faceValue: number; paymentMethod?: string; couponCode?: string;
      createdAt: string; checkedInCount?: number;
      event?: {id: string; eventName?: string; eventDate?: string; category?: string; venueAddress?: string};
      user?: {id: string; firstName: string; lastName: string; email: string};
    }[]>('/events/admin/tickets'),

  // Service Categories — admin uses /all so disabled ones stay visible/toggleable
  getServiceCategories: () =>
    request<ServiceCategory[]>('/services/categories/all'),

  createServiceCategory: (data: {label: string; order?: number; image?: File | null}) => {
    const form = new FormData()
    form.append('label', data.label)
    if (data.order != null) form.append('order', String(data.order))
    if (data.image) form.append('image', data.image)
    return multipart<ServiceCategory>('/services/categories', 'POST', form)
  },

  updateServiceCategory: (id: string, data: {label?: string; order?: number; isActive?: boolean; image?: File | null; removeImage?: boolean}) => {
    const form = new FormData()
    if (data.label != null) form.append('label', data.label)
    if (data.order != null) form.append('order', String(data.order))
    if (data.isActive != null) form.append('isActive', String(data.isActive))
    if (data.image) form.append('image', data.image)
    if (data.removeImage) form.append('removeImage', 'true')
    return multipart<ServiceCategory>(`/services/categories/${id}`, 'PATCH', form)
  },

  deleteServiceCategory: (id: string) =>
    request<{deleted: true}>(`/services/categories/${id}`, {method: 'DELETE'}),

  // Service Providers
  getProviders: (params?: {category?: string; search?: string}) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([,v]) => v) as [string,string][]).toString() : ''
    return request<{
      id: string; name: string; specialty: string; rating: number; reviews: number;
      price: string; location: string; photo: string; available: boolean;
      serviceCategory?: {id: string; label: string; icon: string};
      createdAt: string;
    }[]>(`/services/providers${qs}`)
  },

  // Host Applications
  getHostApplications: () =>
    request<HostApplication[]>('/host-applications'),

  approveHostApplication: (id: string) =>
    request<HostApplication>(`/host-applications/${id}/approve`, {method: 'PATCH'}),

  finalApproveHostApplication: (id: string) =>
    request<HostApplication>(`/host-applications/${id}/final-approve`, {method: 'PATCH'}),

  rejectHostApplication: (id: string, reason?: string) =>
    request<HostApplication>(`/host-applications/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({reason}),
    }),

  suspendProvider: (id: string, reason?: string) =>
    request<HostApplication>(`/host-applications/${id}/suspend`, {
      method: 'PATCH',
      body: JSON.stringify({reason}),
    }),

  reactivateProvider: (id: string) =>
    request<HostApplication>(`/host-applications/${id}/reactivate`, {method: 'PATCH'}),

  deleteProvider: (id: string) =>
    request<{deleted: true}>(`/host-applications/${id}`, {method: 'DELETE'}),

  updateHostVerification: (id: string, flags: {
    verifiedBusinessLicense?: boolean
    verifiedInsurance?: boolean
    verifiedGovId?: boolean
    verifiedCertification?: boolean
    verifiedBackgroundCheck?: boolean
  }) =>
    request<HostApplication>(`/host-applications/${id}/verification`, {
      method: 'PATCH',
      body: JSON.stringify(flags),
    }),

  // Profile
  updateProfile: (data: {firstName?: string; lastName?: string; phone?: string}) =>
    request<{id: string; firstName: string; lastName: string; email: string; phone: string}>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{message: string}>('/users/me/password', {
      method: 'PATCH',
      body: JSON.stringify({currentPassword, newPassword}),
    }),

  uploadPhoto: async (file: File) => {
    const form = new FormData()
    form.append('photo', file)
    const res = await fetch(`${BASE_URL}/users/me/photo`, {
      method: 'POST',
      headers: authToken ? {Authorization: `Bearer ${authToken}`} : {},
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new ApiError(res.status, err?.message ?? `HTTP ${res.status}`)
    }
    return res.json() as Promise<{id: string; firstName: string; lastName: string; email: string; photo: string}>
  },

  // Platform tax config (on/off + rate %). Applies to ticket purchases + bookings.
  getTaxSettings: () =>
    request<{enabled: boolean; ratePercent: number}>('/settings/tax'),

  updateTaxSettings: (enabled: boolean, ratePercent: number) =>
    request<{enabled: boolean; ratePercent: number}>('/settings/tax', {
      method: 'PUT',
      body: JSON.stringify({enabled, ratePercent}),
    }),

  // Platform payout commission (%) — deducted from organizer + provider payouts.
  getCommissionSettings: () =>
    request<{ratePercent: number}>('/settings/commission'),

  updateCommissionSettings: (ratePercent: number) =>
    request<{ratePercent: number}>('/settings/commission', {
      method: 'PUT',
      body: JSON.stringify({ratePercent}),
    }),

  // Notice shown under the event photo grid in the mobile app. Empty text hides it.
  getPhotoDownloadNotice: () =>
    request<{text: string}>('/settings/photo-download-notice'),

  updatePhotoDownloadNotice: (text: string) =>
    request<{text: string}>('/settings/photo-download-notice', {
      method: 'PUT',
      body: JSON.stringify({text}),
    }),

  // Coupons — admin uses /admin/all so inactive ones stay visible/toggleable.
  getCoupons: () =>
    request<Coupon[]>('/coupons/admin/all'),

  createCoupon: (data: CouponInput) =>
    request<Coupon>('/coupons', {method: 'POST', body: JSON.stringify(data)}),

  updateCoupon: (id: string, data: CouponInput) =>
    request<Coupon>(`/coupons/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  deleteCoupon: (id: string) =>
    request<{deleted: true}>(`/coupons/${id}`, {method: 'DELETE'}),

  // Disputes
  getDisputes: () =>
    request<Dispute[]>('/disputes/admin/all'),

  updateDispute: (id: string, data: {status?: string; adminNote?: string}) =>
    request<Dispute>(`/disputes/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  resolveDispute: (id: string, data: {refund?: boolean; refundAmount?: number; note?: string}) =>
    request<Dispute>(`/disputes/${id}/resolve`, {method: 'POST', body: JSON.stringify(data)}),

  // Full dispute detail incl. the linked ticket/booking.
  getDisputeDetail: (id: string) =>
    request<Dispute>(`/disputes/${id}/detail`),

  // Cancel the disputed ticket/booking and reverse all money to the buyer's wallet.
  cancelReverseDispute: (id: string, note?: string) =>
    request<Dispute>(`/disputes/${id}/cancel-reverse`, {method: 'POST', body: JSON.stringify({note})}),

  // Redeem codes (wallet gift/promo codes)
  getRedeemCodes: () =>
    request<RedeemCode[]>('/redeem-codes/admin/all'),

  createRedeemCode: (data: RedeemCodeInput) =>
    request<RedeemCode>('/redeem-codes', {method: 'POST', body: JSON.stringify(data)}),

  updateRedeemCode: (id: string, data: RedeemCodeInput) =>
    request<RedeemCode>(`/redeem-codes/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  deleteRedeemCode: (id: string) =>
    request<{deleted: true}>(`/redeem-codes/${id}`, {method: 'DELETE'}),

  // Payout requests (organizer cash-outs, processed manually)
  getPayouts: () =>
    request<PayoutRequest[]>('/payouts/admin/all'),

  updatePayout: (id: string, data: {status?: string; adminNote?: string}) =>
    request<PayoutRequest>(`/payouts/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  // ── Paginated + server-search list endpoints (one per admin page) ──────────
  // `extra` carries page-specific server-side filters (role, status, category…).
  getUsersPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: string; firstName: string; lastName: string; email: string; phone: string; photo: string; isEmailVerified: boolean; isActive?: boolean; signupMethod?: string | null; role?: {name: string}; interests: string[]; createdAt: string}>>(`/users${pageQS(page, limit, search, extra)}`),

  getEventsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: string; status: string; eventName?: string; eventType?: {id: number; label: string; icon: string}; eventDate?: string; startTime?: string; venueAddress?: string; guestCount?: string; user?: {id: string; firstName: string; lastName: string}; createdAt: string}>>(`/events${pageQS(page, limit, search, extra)}`),

  getBookingsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: string; bookingRef: string; providerName: string; providerCategory: string; subtotal: number; depositAmount: number; remainingAmount: number; status: string; createdAt: string; payments?: {id: string; amount: number; method: string; status: string; type: string}[]; event?: {id: string; eventName?: string; eventDate?: string; venueAddress?: string}; eventType?: {id: number; label: string}; user?: {id: string; firstName: string; lastName: string; email: string}}>>(`/events/admin/bookings${pageQS(page, limit, search, extra)}`),

  getTicketsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: string; ticketRef: string; tier: string; status: string; quantity: number; totalAmount: number; faceValue: number; paymentMethod?: string; couponCode?: string; createdAt: string; checkedInCount?: number; event?: {id: string; eventName?: string; eventDate?: string; category?: string; venueAddress?: string}; user?: {id: string; firstName: string; lastName: string; email: string}}>>(`/events/admin/tickets${pageQS(page, limit, search, extra)}`),

  getProvidersPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: string; name: string; specialty: string; rating: number; reviews: number; price: string; location: string; photo: string; available: boolean; serviceCategory?: {id: string; label: string; icon: string}; createdAt: string}>>(`/services/providers${pageQS(page, limit, search, extra)}`),

  getServiceCategoriesPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<ServiceCategory>>(`/services/categories/all${pageQS(page, limit, search, extra)}`),

  getInterestsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<{id: number; label: string; icon: string; order: number; isActive: boolean}>>(`/interests/all${pageQS(page, limit, search, extra)}`),

  getCouponsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<Coupon>>(`/coupons/admin/all${pageQS(page, limit, search, extra)}`),

  getDisputesPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<Dispute>>(`/disputes/admin/all${pageQS(page, limit, search, extra)}`),

  getRedeemCodesPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<RedeemCode>>(`/redeem-codes/admin/all${pageQS(page, limit, search, extra)}`),

  getPayoutsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<PayoutRequest>>(`/payouts/admin/all${pageQS(page, limit, search, extra)}`),

  getHostApplicationsPaged: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<HostApplication>>(`/host-applications${pageQS(page, limit, search, extra)}`),

  // ── User admin actions (ban = reversible deactivate, verify, role) ─────────
  banUser: (id: string) =>
    request<{id: string; isActive: boolean}>(`/users/${id}/ban`, {method: 'PATCH'}),
  unbanUser: (id: string) =>
    request<{id: string; isActive: boolean}>(`/users/${id}/unban`, {method: 'PATCH'}),
  verifyUser: (id: string) =>
    request<{id: string; isEmailVerified: boolean}>(`/users/${id}/verify`, {method: 'PATCH'}),
  changeUserRole: (id: string, roleName: 'user' | 'host' | 'admin') =>
    request<{id: string; role?: {name: string}}>(`/users/${id}/role`, {method: 'PATCH', body: JSON.stringify({roleName})}),
  updateUser: (id: string, data: {firstName?: string; lastName?: string; email?: string; phone?: string}) =>
    request<{id: string; firstName: string; lastName: string; email: string; phone: string}>(`/users/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),
  deleteUser: (id: string) =>
    request<{deleted: true}>(`/users/${id}`, {method: 'DELETE'}),

  // ── Analytics (admin dashboard aggregations) ───────────────────────────────
  getAnalyticsSummary: () =>
    request<AnalyticsSummary>('/admin/analytics/summary'),
  // Platform commission (service fee) earned: available (past events) / upcoming / total.
  getCommissionEarnings: () =>
    request<{rate: number; available: number; upcoming: number; total: number}>('/admin/analytics/commission'),
  getRevenueSeries: (range: string) =>
    request<{points: TimePoint[]}>(`/admin/analytics/revenue?range=${range}`),
  getUserGrowthSeries: (range: string) =>
    request<{points: TimePoint[]}>(`/admin/analytics/user-growth?range=${range}`),
  getTopEvents: (limit = 10) =>
    request<TopEvent[]>(`/admin/analytics/top-events?limit=${limit}`),
  getProviderPerformance: (limit = 25) =>
    request<ProviderPerformance[]>(`/admin/analytics/provider-performance?limit=${limit}`),

  // Payments & Disputes — transaction history + KPI tiles
  getPaymentTransactions: (page: number, limit: number, search: string, extra?: Record<string, string | undefined>) =>
    request<Paginated<PaymentTransaction>>(`/payments/admin/transactions${pageQS(page, limit, search, extra)}`),
  getPaymentStats: () =>
    request<PaymentStats>('/payments/admin/stats'),
  getRevenueBookings: (range: string) =>
    request<{points: RevenueBookingsPoint[]}>(`/admin/analytics/revenue-bookings?range=${range}`),
  getSignupsByMethod: (range: string) =>
    request<{points: SignupMethodPoint[]}>(`/admin/analytics/signups-by-method?range=${range}`),
  getEventsByCategory: () =>
    request<CategorySlice[]>('/admin/analytics/events-by-category'),

  // Broadcast notifications (audience-targeted, optionally scheduled) + history
  sendBroadcast: (data: {title: string; body: string; audience?: string; scheduledAt?: string | null}) =>
    request<BroadcastNotification>('/admin/notifications/broadcast', {method: 'POST', body: JSON.stringify(data)}),

  getBroadcasts: () =>
    request<BroadcastNotification[]>('/admin/notifications/broadcasts'),
}

export interface BroadcastNotification {
  id: string
  title: string
  body: string
  audience: string
  status: string
  scheduledAt: string | null
  totalDevices: number
  sentCount: number
  failedCount: number
  createdBy: {id: string; firstName: string; lastName: string} | null
  createdAt: string
}

export interface AnalyticsSummary {
  totalRevenue: number
  totalUsers: number
  totalEvents: number
  totalTickets: number
  totalBookings: number
  activeProviders: number
  newUsers30d: number
  revenue30d: number
  avgRevenuePerEvent: number
  revenueGrowth: number
  usersGrowth: number
  eventsGrowth: number
  avgRevenueGrowth: number
}

export interface TimePoint {
  label: string
  value: number
}

export interface RevenueBookingsPoint {
  label: string
  revenue: number
  bookings: number
}

export interface SignupMethodPoint {
  label: string
  email: number
  phone: number
  social: number
}

export interface CategorySlice {
  category: string
  count: number
}

export interface TopEvent {
  id: string
  name: string
  category: string
  ticketsSold: number
  revenue: number
}

export interface PaymentTransaction {
  id: string
  transactionRef: string
  bookingRef: string
  userName: string
  userEmail: string
  amount: number
  serviceFee: number
  method: string
  status: string
  type: string
  createdAt: string
}

export interface PaymentStats {
  totalRevenue: number
  totalTransactions: number
  failedPayments: number
}

export interface ProviderPerformance {
  providerId: string
  providerName: string
  category: string
  bookings: number
  revenue: number
  completed: number
  cancelled: number
  rating?: number
}

export type AdminActivityType = 'signup' | 'event_published' | 'ticket_purchased' | 'service_booked'

export interface AdminActivity {
  type: AdminActivityType
  title: string
  subtitle?: string
  userId?: string
  eventId?: string
  at: string
}

// Base host (without the trailing /api) for the Socket.io connection.
export const SOCKET_URL = BASE_URL.replace(/\/api\/?$/, '')
export const getAdminToken = () => authToken
